import {
  Badge,
  Button,
  Card,
  DataTable,
  TableNumericCell,
  TablePrimaryCell,
} from '@shared/components/ui';
import { getStatusTone, toneToBadgeVariant } from '@shared/config/theme';
import { toneDotClasses } from '@shared/utils/statusTone';

import { useTranslation } from 'react-i18next';

import { useRestoreJobs } from '../hooks/useRestoreJobs';
import { RestoreJobLogsViewer } from '../components/RestoreJobLogsViewer';
import type { RestoreJob } from '../types/restoreJob';
import { normalizeBackupType } from '@features/backups/utils/backupType';
import {
  type LucideIcon,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Clock,
  Database,
  Eye,
  Search,
  HardDrive,
  Activity,
  History,
  Download,
  Server,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Agent {
  id: string;
  name: string;
  status: string;
}

export default function RestoreJobs() {
  const { t, i18n } = useTranslation('restores');
  const locale = i18n.resolvedLanguage || i18n.language;
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [search, setSearch] = useState('');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedJob, setSelectedJob] = useState<RestoreJob | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);

  // Suspend the list refresh while a modal is open: the modal polls the same
  // job's progress, and refreshing behind it doubles the cost for data the user
  // cannot see.
  const { jobs, loading, error, total, page, timezone, fetchJobs, setPage, setFilters } =
    useRestoreJobs({
      status: statusFilter,
      search: search,
      agent_id: agentFilter,
      pollEnabled: !showDetailsModal && !showLogsModal,
    });

  const pageSize = 50;

  const formatCount = (value: number | null | undefined) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value ?? 0);

  const getBackupTypeLabel = (type: string) => {
    const normalizedType = normalizeBackupType(type);
    if (normalizedType === 'FILES') return t('types.files');
    if (normalizedType === 'POSTGRESQL') return t('types.postgresql');
    return type;
  };

  const getToken = useCallback(() => localStorage.getItem('auth_token'), []);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/agents', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      setAgents(Array.isArray(data) ? data : []);
    } catch {
      // Silently fail
    }
  }, [getToken]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  /** Glyph per state; the colour comes from the shared tone contract. */
  const statusIcons: Record<string, LucideIcon> = {
    COMPLETED: Download,
    FAILED: X,
    RUNNING: Activity,
    PENDING: Clock,
  };

  /** Localized label for a restore status; falls back to the raw value. */
  const getStatusLabel = (status: string) => {
    const key = `statuses.${status.toLowerCase()}`;
    const label = t(key);
    return label === key ? status : label;
  };

  const handleReset = () => {
    setSearch('');
    setStatusFilter('');
    setAgentFilter('');
    setFilters({});
  };

  const openDetails = (job: RestoreJob) => {
    setSelectedJob(job);
    setShowDetailsModal(true);
  };

  const openLogs = (job: RestoreJob) => {
    setSelectedJob(job);
    setShowLogsModal(true);
  };

  return (
    <>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
              {t('title')}
            </h1>
            <p className="text-text-muted-light dark:text-text-muted-dark font-medium">
              {t('subtitle')}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="info" className="min-h-6 h-auto max-w-full whitespace-nowrap">
              {t('timezoneLabel')}: {timezone}
            </Badge>
            {(!error || jobs.length > 0) && (
              <Badge variant="neutral" className="min-h-6 h-auto max-w-full whitespace-nowrap">
                {t('totalJobs', { count: total })}
              </Badge>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle-light dark:text-text-subtle-dark w-4 h-4" />
              <input
                type="text"
                placeholder={t('searchPlaceholder')}
                aria-label={t('searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) =>
                  e.key === 'Enter' &&
                  setFilters({ status: statusFilter, search, agent_id: agentFilter })
                }
                className="w-full min-h-11 pl-10 pr-4 py-2 bg-surface-2-light dark:bg-surface-2-dark border-none rounded-xl text-sm focus:ring-2 focus:ring-brand-500 transition-all"
              />
            </div>
            <select
              aria-label={t('allStatuses')}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setFilters({ status: e.target.value, search, agent_id: agentFilter });
              }}
              className="min-h-11 px-4 py-2 bg-surface-2-light dark:bg-surface-2-dark border-none rounded-xl text-sm font-bold text-slate-600 dark:text-slate-300 focus:ring-2 focus:ring-brand-500"
            >
              <option value="">{t('allStatuses')}</option>
              <option value="COMPLETED">{t('statuses.completed')}</option>
              <option value="FAILED">{t('statuses.failed')}</option>
              <option value="RUNNING">{t('statuses.running')}</option>
              <option value="PENDING">{t('statuses.pending')}</option>
            </select>
            <select
              aria-label={t('filters.allAgents')}
              value={agentFilter}
              onChange={(e) => {
                setAgentFilter(e.target.value);
                setFilters({ status: statusFilter, search, agent_id: e.target.value });
              }}
              className="min-h-11 px-4 py-2 bg-surface-2-light dark:bg-surface-2-dark border-none rounded-xl text-sm font-bold text-slate-600 dark:text-slate-300 focus:ring-2 focus:ring-brand-500"
            >
              <option value="">{t('filters.allAgents')}</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <Button variant="secondary" size="md" onClick={handleReset} className="md:w-auto">
              <RotateCcw className="w-4 h-4 mr-2" /> {t('reset')}
            </Button>
          </div>
        </Card>

        {/* Loading State */}
        {loading && (
          <div className="py-20 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto mb-4"></div>
            <p className="text-text-subtle-light dark:text-text-subtle-dark font-bold uppercase tracking-widest text-xs">
              {t('job.fetchingRecords')}
            </p>
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div role="alert" aria-live="assertive">
            <Card className="p-6 border-l-4 border-l-state-danger-light dark:border-l-state-danger-dark">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                    {t('job.loadErrorTitle')}
                  </h3>
                  <p className="text-text-muted-light dark:text-text-muted-dark">
                    {t('job.loadErrorDescription')}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => {
                    void fetchJobs();
                  }}
                  className="sm:shrink-0"
                >
                  <RotateCcw className="w-4 h-4" />
                  {t('job.retryLoading')}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && jobs.length === 0 && (
          <Card className="py-20 text-center">
            <Database className="w-16 h-16 text-surface-border-light dark:text-surface-border-dark mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">
              {t('job.noJobsFound')}
            </h3>
            <p className="text-text-muted-light dark:text-text-muted-dark">
              {t('job.tryAdjustingFilters')}
            </p>
          </Card>
        )}

        {/* Jobs List */}
        {!loading && jobs.length > 0 && (
          <>
            {/* Compact card layout — phones and small tablets */}
            <div className="space-y-2 lg:hidden">
              {jobs.map((job) => (
                <Card key={job.id} className="p-0 overflow-hidden group">
                  <div className="flex items-stretch">
                    <div className={`w-2 ${toneDotClasses[getStatusTone(job.status)]}`} />
                    <div className="flex-1 p-4 space-y-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-xl bg-surface-2-light dark:bg-surface-2-dark flex items-center justify-center text-text-subtle-light dark:text-text-subtle-dark">
                          {(() => {
                            const StatusIcon = statusIcons[job.status] ?? HardDrive;
                            return <StatusIcon className="w-5 h-5" />;
                          })()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <h3
                              title={job.backup_config_name}
                              className="font-black text-slate-900 dark:text-white uppercase tracking-tight truncate"
                            >
                              {job.backup_config_name}
                            </h3>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/backups/configs/${job.backup_config_id}/snapshots`);
                              }}
                              className="min-h-11 min-w-11 shrink-0 rounded hover:bg-surface-border-light dark:hover:bg-surface-border-dark transition-colors opacity-100 focus-visible:opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
                              aria-label={t('viewSnapshots')}
                              title={t('viewSnapshots')}
                            >
                              <Eye
                                size={14}
                                className="text-text-subtle-light hover:text-brand-600 dark:text-text-subtle-dark dark:hover:text-brand-400"
                              />
                            </button>
                            <Badge variant={toneToBadgeVariant[getStatusTone(job.status)]}>
                              {getStatusLabel(job.status)}
                            </Badge>
                          </div>
                          <div className="mt-1 flex items-center space-x-2 text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest">
                            <span className="flex items-center">
                              <Server size={10} className="mr-1" />{' '}
                              {getBackupTypeLabel(job.backup_config_type)}
                            </span>
                            <span className="flex items-center">
                              <Activity size={10} className="mr-1" /> {job.triggered_by_user_name}
                            </span>
                          </div>
                        </div>
                      </div>

                      {(job.status === 'RUNNING' || job.status === 'PENDING') && (
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-text-muted-light dark:text-text-muted-dark truncate">
                              {job.current_file || t('job.processing')}
                            </span>
                            <span className="font-medium text-slate-700 dark:text-slate-300">
                              {Math.round(job.progress_percent || 0)}%
                            </span>
                          </div>
                          <div className="w-full bg-surface-border-light dark:bg-surface-border-dark rounded-full h-1.5">
                            <div
                              className="bg-state-info-light dark:bg-state-info-dark h-1.5 rounded-full transition-all duration-300"
                              style={{ width: `${job.progress_percent || 0}%` }}
                            />
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mb-1">
                            {t('job.filesRestored')}
                          </p>
                          <p className="text-sm font-black text-slate-700 dark:text-slate-200">
                            {formatCount(job.files_restored)}
                            {job.dirs_created != null && job.dirs_created > 0 && (
                              <span className="text-xs font-normal text-text-subtle-light dark:text-text-subtle-dark ml-1">
                                ({t('job.inDirs', { dirs: formatCount(job.dirs_created) })})
                              </span>
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mb-1">
                            {t('job.location')}
                          </p>
                          <p className="text-sm font-black text-slate-700 dark:text-slate-200 break-words">
                            {job.restore_target?.location === 'custom'
                              ? t('wizard.customLocation')
                              : t('wizard.originalLocation')}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mb-1">
                            {t('job.duration')}
                          </p>
                          <p className="text-sm font-black text-slate-700 dark:text-slate-200">
                            {job.duration_seconds ? `${job.duration_seconds}s` : '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mb-1">
                            {t('job.created')}
                          </p>
                          <p className="text-sm font-black text-slate-700 dark:text-slate-200">
                            {new Date(job.created_at).toLocaleDateString(locale)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 pt-2">
                        <Button
                          variant="outline"
                          size="md"
                          onClick={() => openDetails(job)}
                          className="flex-1"
                        >
                          {t('job.details')}
                        </Button>
                        <Button
                          variant="secondary"
                          size="md"
                          onClick={() => openLogs(job)}
                          className="flex-1"
                        >
                          {t('job.logs')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Tabular layout — desktop. Content-sized columns; the identity
                column absorbs slack so localized labels are never clipped. */}
            <div className="hidden lg:block">
              <DataTable
                caption={t('title')}
                rows={jobs}
                rowKey={(job) => job.id}
                rowAccent={(job) => toneDotClasses[getStatusTone(job.status)]}
                columns={[
                  {
                    key: 'job',
                    header: t('title'),
                    width: 'w-full',
                    cell: (job) => (
                      <TablePrimaryCell
                        title={job.backup_config_name}
                        trailing={
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/backups/configs/${job.backup_config_id}/snapshots`);
                            }}
                            className="min-h-11 min-w-11 rounded opacity-0 transition-colors group-hover:opacity-100 focus-visible:opacity-100 hover:bg-surface-2-light dark:hover:bg-surface-2-dark"
                            title={t('viewSnapshots')}
                            aria-label={t('viewSnapshots')}
                          >
                            <Eye
                              size={14}
                              className="text-text-subtle-light hover:text-brand-600 dark:text-text-subtle-dark dark:hover:text-brand-400"
                            />
                          </button>
                        }
                        meta={
                          <>
                            <span className="flex items-center gap-1">
                              <Server size={12} /> {getBackupTypeLabel(job.backup_config_type)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={12} />{' '}
                              {new Date(job.created_at).toLocaleTimeString(locale)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Activity size={12} /> {job.triggered_by_user_name}
                            </span>
                          </>
                        }
                      />
                    ),
                  },
                  {
                    key: 'status',
                    header: t('list.columns.status'),
                    width: 'w-[132px]',
                    cell: (job) => (
                      <Badge variant={toneToBadgeVariant[getStatusTone(job.status)]}>
                        {getStatusLabel(job.status)}
                      </Badge>
                    ),
                  },
                  {
                    key: 'files',
                    header: t('job.filesRestored'),
                    align: 'right',
                    cell: (job) => (
                      <TableNumericCell
                        value={formatCount(job.files_restored)}
                        hint={
                          job.dirs_created && job.dirs_created > 0
                            ? t('job.inDirs', { dirs: formatCount(job.dirs_created) })
                            : undefined
                        }
                      />
                    ),
                  },
                  {
                    key: 'location',
                    header: t('job.location'),
                    align: 'right',
                    cell: (job) => (
                      <span className="whitespace-nowrap text-sm font-bold text-slate-700 dark:text-slate-200">
                        {job.restore_target?.location === 'custom'
                          ? t('wizard.customLocation')
                          : t('wizard.originalLocation')}
                      </span>
                    ),
                  },
                  {
                    key: 'duration',
                    header: t('job.duration'),
                    align: 'right',
                    hideBelow: 'xl',
                    cell: (job) => (
                      <TableNumericCell
                        value={job.duration_seconds ? `${job.duration_seconds}s` : '—'}
                      />
                    ),
                  },
                  {
                    key: 'created',
                    header: t('job.created'),
                    align: 'right',
                    hideBelow: 'xl',
                    cell: (job) => (
                      <span className="whitespace-nowrap text-sm font-bold tabular-nums text-slate-700 dark:text-slate-200">
                        {new Date(job.created_at).toLocaleDateString(locale)}
                      </span>
                    ),
                  },
                  {
                    key: 'actions',
                    header: '',
                    align: 'right',
                    width: 'w-[190px]',
                    cell: (job) => (
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="outline" size="md" onClick={() => openDetails(job)}>
                          {t('job.details')}
                        </Button>
                        <Button variant="secondary" size="md" onClick={() => openLogs(job)}>
                          {t('job.logs')}
                        </Button>
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          </>
        )}

        {/* Pagination */}
        {!loading && jobs.length > 0 && (
          <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest">
              {t('job.showingResults', { count: jobs.length, total })}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="md"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                className="min-h-11"
              >
                <ChevronLeft size={16} className="mr-1" /> {t('job.prev')}
              </Button>
              <div className="min-h-11 min-w-11 flex items-center justify-center px-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-xs font-black">
                {page}
              </div>
              <Button
                variant="secondary"
                size="md"
                disabled={jobs.length < pageSize}
                onClick={() => setPage(page + 1)}
                className="min-h-11"
              >
                {t('job.next')} <ChevronRight size={16} className="ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedJob && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="restore-job-details-title"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
        >
          <Card className="w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-300 shadow-elevated">
            <div className="p-4 sm:p-8 space-y-6 sm:space-y-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 bg-accent-soft-light dark:bg-accent-soft-dark rounded-2xl flex items-center justify-center text-brand-600 dark:text-brand-400">
                    <Database size={28} />
                  </div>
                  <div>
                    <h2
                      id="restore-job-details-title"
                      className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight"
                    >
                      {selectedJob.backup_config_name}
                    </h2>
                    <Badge variant={toneToBadgeVariant[getStatusTone(selectedJob.status)]}>
                      {getStatusLabel(selectedJob.status)}
                    </Badge>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  aria-label={t('selective.actions.close')}
                  className="min-h-11 min-w-11 p-2 hover:bg-surface-2-light dark:hover:bg-surface-2-dark rounded-xl transition-colors"
                >
                  <X className="w-6 h-6 text-text-subtle-light dark:text-text-subtle-dark" />
                </button>
              </div>

              {/* Progress section for running jobs */}
              {(selectedJob.status === 'RUNNING' || selectedJob.status === 'PENDING') && (
                <div className="p-6 bg-state-info-bg-light dark:bg-state-info-bg-dark border border-state-info-light/20 dark:border-state-info-dark/20 rounded-2xl space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-black text-state-info-light dark:text-state-info-dark uppercase tracking-[0.2em] mb-1">
                        {t('job.restoreProgress')}
                      </p>
                      <p className="text-sm font-medium text-state-info-light dark:text-state-info-dark">
                        {selectedJob.current_file || t('job.processing')}
                      </p>
                    </div>
                    <span className="text-2xl font-black text-state-info-light dark:text-state-info-dark">
                      {Math.round(selectedJob.progress_percent || 0)}%
                    </span>
                  </div>
                  <div className="w-full bg-state-info-bg-light dark:bg-state-info-bg-dark rounded-full h-3">
                    <div
                      className="bg-state-info-light dark:bg-state-info-dark h-3 rounded-full transition-all duration-500"
                      style={{ width: `${selectedJob.progress_percent || 0}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-5 sm:gap-y-8 gap-x-12">
                <DetailItem label={t('job.restoreJobId')} value={selectedJob.id} mono />
                <DetailItem label={t('job.backupJobId')} value={selectedJob.backup_job_id} mono />
                <DetailItem
                  label={t('job.startedAt')}
                  value={
                    selectedJob.started_at
                      ? new Date(selectedJob.started_at).toLocaleString(locale)
                      : '-'
                  }
                />
                <DetailItem
                  label={t('job.completedAt')}
                  value={
                    selectedJob.completed_at
                      ? new Date(selectedJob.completed_at).toLocaleString(locale)
                      : '-'
                  }
                />
                <DetailItem
                  label={t('job.duration')}
                  value={`${selectedJob.duration_seconds || 0} ${t('job.seconds')}`}
                />
                <DetailItem
                  label={t('job.filesRestored')}
                  value={`${formatCount(selectedJob.files_restored)}${selectedJob.dirs_created && selectedJob.dirs_created > 0 ? ` (${t('job.inDirs', { dirs: formatCount(selectedJob.dirs_created) })})` : ''}`}
                />
                <DetailItem
                  label={t('job.triggeredBy')}
                  value={selectedJob.triggered_by_user_name}
                />
                <DetailItem
                  label={t('job.restoreLocation')}
                  value={
                    selectedJob.restore_target?.location === 'custom'
                      ? t('job.customPath')
                      : t('job.originalLocation')
                  }
                />
                {selectedJob.restore_target?.custom_path && (
                  <div className="sm:col-span-2">
                    <DetailItem
                      label={t('job.customPathLabel')}
                      value={selectedJob.restore_target.custom_path}
                    />
                  </div>
                )}
              </div>

              {selectedJob.status === 'FAILED' && selectedJob.error_message && (
                <div className="p-4 bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 rounded-2xl">
                  <p className="text-[10px] font-black text-state-danger-light dark:text-state-danger-dark uppercase tracking-[0.2em] mb-1">
                    {t('job.errorReport')}
                  </p>
                  <p className="text-sm font-medium text-state-danger-light dark:text-state-danger-dark leading-relaxed">
                    {selectedJob.error_message}
                  </p>
                </div>
              )}

              <div className="flex flex-wrap gap-3 pt-4 border-t border-surface-border-light dark:border-surface-border-dark">
                <Button
                  variant="secondary"
                  size="md"
                  className="flex-1"
                  onClick={() => {
                    setShowDetailsModal(false);
                    openLogs(selectedJob);
                  }}
                >
                  <History size={16} className="mr-2" /> {t('job.viewLogs')}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Logs Modal */}
      {showLogsModal && selectedJob && (
        <RestoreJobLogsViewer
          isOpen={showLogsModal}
          onClose={() => setShowLogsModal(false)}
          restoreJobId={selectedJob.id}
          backupConfigName={selectedJob.backup_config_name}
        />
      )}
    </>
  );
}

function DetailItem({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-[0.2em] mb-1.5">
        {label}
      </p>
      <p
        className={`text-sm font-bold text-slate-900 dark:text-white ${
          mono ? 'font-mono break-all' : ''
        }`}
        title={value}
      >
        {value}
      </p>
    </div>
  );
}
